import React, { useState } from 'react';
import { BookOpen, GraduationCap, Plus } from 'lucide-react';

import { staffApi } from '../../services/staffApi';
import AddClassTeacherAssignmentModal from './AddClassTeacherAssignmentModal';
import AddSubjectAssignmentModal from './AddSubjectAssignmentModal';

const EmptyState = ({ icon: Icon, title, message }) => (
  <div className="p-10 text-center">
    <Icon className="mx-auto h-9 w-9 text-gray-300" />
    <p className="mt-3 font-semibold text-gray-700">{title}</p>
    <p className="mt-1 text-sm text-gray-500">{message}</p>
  </div>
);

const StaffAssignmentsTab = ({
  formData,
  schoolId,
  staffId,
  loadStaff,
  showToast,
}) => {
  const [activeAssignmentTab, setActiveAssignmentTab] = useState('classTeacher');
  const [showClassTeacherModal, setShowClassTeacherModal] = useState(false);
  const [showSubjectModal, setShowSubjectModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const classTeacherAssignments = formData.classTeacherAssignments || [];
  const subjectAssignments = formData.subjectAssignments || [];

  const handleClassTeacherAssignment = async (assignmentData) => {
    if (saving) return;
    setSaving(true);

    try {
      await staffApi.assignClassTeacher(schoolId, staffId, {
        academic_year: assignmentData.academicYear,
        class_level: assignmentData.classLevel,
        section: assignmentData.section,
        assigned_date: assignmentData.assignedDate,
        notes: assignmentData.notes,
        status: assignmentData.status,
      });

      setShowClassTeacherModal(false);
      await loadStaff();
      showToast('Class teacher assignment created successfully.', 'success');
    } catch (error) {
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const handleSubjectAssignment = async (assignmentData) => {
    if (saving) return;
    setSaving(true);

    try {
      await staffApi.assignSubject(schoolId, staffId, {
        academic_year: assignmentData.academicYear,
        class_level: assignmentData.classLevel,
        section: assignmentData.section,
        subject: assignmentData.subject,
        status: assignmentData.status,
      });

      setShowSubjectModal(false);
      await loadStaff();
      showToast('Subject assignment created successfully.', 'success');
    } catch (error) {
      throw error;
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200">
        <div className="flex gap-8">
          <button
            type="button"
            onClick={() => setActiveAssignmentTab('classTeacher')}
            className={`pb-4 text-sm font-semibold ${
              activeAssignmentTab === 'classTeacher'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-500'
            }`}
          >
            Class Teacher Assignments
          </button>

          <button
            type="button"
            onClick={() => setActiveAssignmentTab('subject')}
            className={`pb-4 text-sm font-semibold ${
              activeAssignmentTab === 'subject'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-500'
            }`}
          >
            Subject Assignments
          </button>
        </div>
      </div>

      {activeAssignmentTab === 'classTeacher' && (
        <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
          <div className="flex items-center justify-between border-b border-gray-100 px-6 py-5">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Class Teacher Assignments
              </h3>
              <p className="mt-1 text-xs text-gray-500">
                Year, class, and section are validated by the backend.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowClassTeacherModal(true)}
              className="flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-medium text-white hover:bg-blue-700"
            >
              <Plus size={18} />
              Add Assignment
            </button>
          </div>

          {classTeacherAssignments.length === 0 ? (
            <EmptyState
              icon={GraduationCap}
              title="No class teacher assignments"
              message="Add an assignment for a specific academic year, class, and section."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-6 py-4 font-medium">Academic Year</th>
                    <th className="px-6 py-4 font-medium">Class</th>
                    <th className="px-6 py-4 font-medium">Section</th>
                    <th className="px-6 py-4 font-medium">Assigned Date</th>
                    <th className="px-6 py-4 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {classTeacherAssignments.map((item) => (
                    <tr key={item.id} className="border-t border-gray-100">
                      <td className="px-6 py-4">{item.academicYearName || '—'}</td>
                      <td className="px-6 py-4 font-medium text-gray-900">{item.className || '—'}</td>
                      <td className="px-6 py-4">{item.sectionName || '—'}</td>
                      <td className="px-6 py-4">{item.assignedDate || '—'}</td>
                      <td className="px-6 py-4">{item.status || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {activeAssignmentTab === 'subject' && (
        <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
          <div className="flex items-center justify-between border-b border-gray-100 px-6 py-5">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Subject Assignments
              </h3>
              <p className="mt-1 text-xs text-gray-500">
                Subjects are filtered for the selected year and class.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowSubjectModal(true)}
              className="flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-medium text-white hover:bg-blue-700"
            >
              <Plus size={18} />
              Add Assignment
            </button>
          </div>

          {subjectAssignments.length === 0 ? (
            <EmptyState
              icon={BookOpen}
              title="No subject assignments"
              message="Assign a subject for a selected academic year, class, and section."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[820px] text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-6 py-4 font-medium">Academic Year</th>
                    <th className="px-6 py-4 font-medium">Class</th>
                    <th className="px-6 py-4 font-medium">Section</th>
                    <th className="px-6 py-4 font-medium">Subject</th>
                    <th className="px-6 py-4 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {subjectAssignments.map((item) => (
                    <tr key={item.id} className="border-t border-gray-100">
                      <td className="px-6 py-4">{item.academicYearName || '—'}</td>
                      <td className="px-6 py-4">{item.className || '—'}</td>
                      <td className="px-6 py-4">{item.sectionName || '—'}</td>
                      <td className="px-6 py-4 font-medium text-gray-900">{item.subjectName || '—'}</td>
                      <td className="px-6 py-4">{item.status || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <AddClassTeacherAssignmentModal
        isOpen={showClassTeacherModal}
        onClose={() => setShowClassTeacherModal(false)}
        onSubmit={handleClassTeacherAssignment}
        loading={saving}
        schoolId={schoolId}
      />

      <AddSubjectAssignmentModal
        isOpen={showSubjectModal}
        onClose={() => setShowSubjectModal(false)}
        onSubmit={handleSubjectAssignment}
        loading={saving}
        schoolId={schoolId}
      />
    </div>
  );
};

export default StaffAssignmentsTab;
